/**
 * useEmployees Hook
 * Provides access to employee data with derived computations
 */

import { useAtom } from "jotai";
import { useMemo, useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { employeesQueryAtom } from "@/store/referenceDataStore";
import { adminAPI } from "@/services";
import type { IServiceEmployee, IServiceEmployeeSettings } from "@/services";
import {
  applyNameFallback,
  fallbackFromEmployee,
} from "@/hooks/data/dataQuality";
import { invalidateWithTimeout } from "@/utils/queryUtils";

export interface UseEmployeesReturn {
  /** All employees from API */
  employees: IServiceEmployee[];
  /** Employees filtered to bookable only */
  bookableEmployees: IServiceEmployee[];
  /** Employees sorted alphabetically by name */
  sortedEmployees: IServiceEmployee[];
  /** Active (non-archived) employees */
  activeEmployees: IServiceEmployee[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  error: Error | null;
  /** Refetch function */
  refetch: () => void;
  /** Find employee by ID */
  findById: (id: number) => IServiceEmployee | undefined;
  /** Find employee by email */
  findByEmail: (email: string) => IServiceEmployee | undefined;
  /** Update employee settings (is_bookable) */
  updateEmployeeSettings: (
    employeeId: number,
    settings: IServiceEmployeeSettings,
  ) => Promise<IServiceEmployeeSettings | null>;
  /** Updating settings state */
  isUpdatingSettings: boolean;
  /** Update settings error */
  updateSettingsError: Error | null;
}

export function useEmployees(): UseEmployeesReturn {
  const [query] = useAtom(employeesQueryAtom);
  const queryClient = useQueryClient();
  const [isUpdatingSettings, setIsUpdatingSettings] = useState(false);
  const [updateSettingsError, setUpdateSettingsError] = useState<Error | null>(
    null,
  );

  // Stable reference to prevent unnecessary recalculations
  const rawEmployees = useMemo(() => query.data ?? [], [query.data]);
  const employees = useMemo(
    () =>
      rawEmployees.map((employee) =>
        applyNameFallback(employee, fallbackFromEmployee(employee)),
      ),
    [rawEmployees],
  );

  // Bookable employees only
  const bookableEmployees = useMemo(
    () => employees.filter((e) => e.settings?.is_bookable !== false),
    [employees],
  );

  // Sort alphabetically by full name
  const sortedEmployees = useMemo(
    () =>
      [...employees].sort((a, b) =>
        `${a.given_name} ${a.surname}`.localeCompare(
          `${b.given_name} ${b.surname}`,
        ),
      ),
    [employees],
  );

  // Active (non-archived) employees
  const activeEmployees = useMemo(
    () => employees.filter((e) => !e.is_archived),
    [employees],
  );

  // Find by ID
  const findById = useCallback(
    (id: number) => employees.find((e) => e.id === id),
    [employees],
  );

  // Find by email
  const findByEmail = useCallback(
    (email: string) => employees.find((e) => e.email === email),
    [employees],
  );

  // Update employee settings (is_bookable)
  const updateEmployeeSettings = useCallback(
    async (
      employeeId: number,
      settings: IServiceEmployeeSettings,
    ): Promise<IServiceEmployeeSettings | null> => {
      setIsUpdatingSettings(true);
      setUpdateSettingsError(null);

      try {
        const result = await adminAPI.updateEmployeeSettings(
          employeeId,
          settings,
        );
        // Invalidate and refetch employees to get updated settings
        await invalidateWithTimeout(queryClient, ["employees"]);
        return result;
      } catch (error) {
        setUpdateSettingsError(error as Error);
        return null;
      } finally {
        setIsUpdatingSettings(false);
      }
    },
    [queryClient],
  );

  return {
    employees,
    bookableEmployees,
    sortedEmployees,
    activeEmployees,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: query.refetch,
    findById,
    findByEmail,
    updateEmployeeSettings,
    isUpdatingSettings,
    updateSettingsError,
  };
}
